/**
 * Factory for WebSked publications, now featuring sections
 * @author Brendan Magee
 */

function PublicationFactory(
  $http,
  $q,
  $translate,
  analyticsService,
  confirmationDialog,
  toastWrapperService,
  PublicationSection
) {
  const descriptionLimit = 450;

  const errorMessages = {
    descriptionLength: $translate.instant('DESCRIPTION_LIMIT_EXCEEDED_ERROR', { descriptionLimit }),
    duplicateSectionIds: $translate.instant('DUPLICATE_SECTION_IDS'),
    nameMissing: $translate.instant('PUBLICATION_NAME_ERROR'),
    sectionError: $translate.instant('PUBLICATION_SECTION_ERROR'),
    websiteMissing: `${$translate.instant('MULTISITE.WEBSITE_REQUIRED')}.`
  };

  class EditionTimeError extends Error {
    constructor(message = '') {
      super(message);
      this.name = 'EditionTimeError';
    }
  }

  return class Publication {
    constructor(data = {}) {
      const publicationData = data;
      const publicationId = data.id || null;

      if (data.sections && data.sections.length) {
        publicationData.sections = data.sections.map(section => (
          new PublicationSection({ publicationId, ...section })));
      }

      Object.assign(this, Publication.defaultProperties, publicationData);
    }

    save() {
      if (this.error) {
        return $q.reject(this.error);
      }

      return $http.post('api/publications', this.payload)
        .then((response) => {
          const newPublication = new Publication(response.data);
          Publication.cache = newPublication;
          analyticsService.trackEvent('Publication management', 'publication created');
          return newPublication;
        });
    }

    update() {
      if (this.error) {
        return $q.reject(this.error);
      }

      return $http.put(`api/publications/${this.id}`, this.payload)
        .then((response) => {
          const updatedPublication = new Publication(response.data);
          Publication.cache = updatedPublication;
          analyticsService.trackEvent('Publication management', 'publication updated');
          return updatedPublication;
        });
    }

    confirmDeletion() {
      confirmationDialog.confirm('CONFIRM_DELETE_PUBLICATION_WARNING', this.delete.bind(this));
    }

    delete() {
      return $http.delete(`api/publications/${this.id}`)
        .then((response) => {
          this.deleted = true;
          toastWrapperService.createToast(Publication.messages.deleteSuccess, 'success');
          analyticsService.trackEvent('Publication management', 'publication deleted');
          return response.data;
        }, () => {
          toastWrapperService.createToast(Publication.messages.deleteError, 'danger');
        });
    }

    getEditionTimes({ startDate, endDate, numEditions }) {
      if (!startDate) {
        throw new EditionTimeError('This method requires a `startDate` parameter');
      }

      if (!endDate && !numEditions) {
        throw new EditionTimeError('This method requires either an `endDate` or `numEditions` parameter');
      }

      const params = { startDate, endDate, numEditions };
      Object.keys(params).forEach((prop) => {
        if (!params[prop]) {
          params[prop] = null;
        }
      });

      return $http.get(`api/publications/${this.id}/editionTimes`, { params })
        .then(response => response.data);
    }

    validateData() {
      const validationTests = {
        descriptionLength: this.description && this.description.length > descriptionLimit,
        duplicateSectionIds: this.sections.length !== new Set(this.sections.map(s => s.id)).size,
        nameMissing: !this.name || !this.name.length,
        sectionError: !!this.sections.filter(section => section.error).length,
        websiteMissing: !this.website
      };

      const errorMessage = Object.keys(validationTests).reduce((acc, key) => {
        let error = acc;
        if (validationTests[key]) {
          error += `${errorMessages[key]} `;
        }
        return error;
      }, '');

      return errorMessage ? `${$translate.instant('PUBLICATION_DATA_INVALID')}: ${errorMessage}`.trim() : null;
    }

    addSection() {
      this.sections.push(new PublicationSection());
      return this;
    }

    removeSection() {
      this.sections.pop();
      return this;
    }

    get error() {
      return this.validateData();
    }

    get payload() {
      const payload = { ...this };
      payload.sections = this.sections.map(section => section.payload);
      return payload;
    }

    static get cache() {
      return !this.cacheObj ? {} : this.cacheObj;
    }

    static set cache(val) {
      if (!this.cacheObj) {
        this.cacheObj = {};
        this.cacheObj[val.id] = val;
      } else {
        this.cacheObj[val.id] = val;
      }
    }

    static get defaultProperties() {
      return {
        name: '',
        description: '',
        publicationSetId: null,
        website: '',
        sections: [
          new PublicationSection()
        ]
      };
    }

    static get messages() {
      return {
        deleteSuccess: $translate.instant('SUCCESSFULLY_DELETED_PUBLICATION'),
        deleteError: $translate.instant('ERROR_DELETING_PUBLICATION')
      };
    }

    static get(publicationId) {
      if (Publication.cache[publicationId]) {
        return $q.when(this.cache[publicationId]);
      }

      return $http.get(`api/publications/${publicationId}`)
        .then(response => new Publication(response.data));
    }

    static getPublicationsList(params = {}) {
      if (params.size !== undefined && params.size < 1) {
        return $q.reject('Page size param must be at least 1');
      }

      return $http.get('api/publications', { params })
        .then((response) => {
          const { results, pageSize } = response.data;
          const publications = Array.isArray(results)
            ? results.map(publication => new Publication(publication))
            : [];
          return { publications, pageSize };
        });
    }

    static async getSets(params = { size: 100 }) {
      if (this.setsCache) {
        return this.setsCache;
      }

      const { data = {} } = await $http.get('api/publications/sets', { params });
      const publicationSetData = { list: [], map: new Map() };

      const setsData = Array.isArray(data.results)
        ? data.results.reduce((dataObj, set) => {
          dataObj.map.set(set.id, set);
          dataObj.list.push(set);
          return dataObj;
        }, publicationSetData)
        : publicationSetData;

      this.setsCache = setsData;
      return setsData;
    }

    static getNumberOfPublications() {
      return $http.get('api/publications/count')
        .then(response => response.data.count);
    }
  };
}

const factoryArray = [
  '$http',
  '$q',
  '$translate',
  'analyticsService',
  'confirmationDialog',
  'toastWrapperService',
  'PublicationSection',
  PublicationFactory
];

export default function (ngModule) {
  ngModule.factory('Publication', factoryArray);
}
